<template>
  <div class="table-row"
       v-if="service">
    <div class="row no-gutters align-items-center">
      <div class="col-1 col-td word-break">
        {{ service.cod || '-' }}
      </div>
      <div class="col-2 col-td break-all visit-line">
        <span v-if="appliedVsTotalStats && !editMode && applied"
              class="circle mr-2"
              :class="`status-${appliedVsTotalStats.status}`"></span>
        {{ service.name }}
      </div>
      <div class="col-2 col-td">
        <OneServiceCostField :service="service"
                             :teeth="teeth"
                             :readonly="readonly"/>
      </div>
      <div class="col-2 col-td">
        <span v-if="appliedVsTotalStats && !editMode">
          {{ applied }} / {{ appliedVsTotalStats.qty }}
        </span>
        <b-dropdown v-else
                    variant="link"
                    toggle-tag="a"
                    menu-class="py-0 dropdown-menu-teeth"
                    no-caret>
          <template #button-content>
            <div>
              <a class="text-body">{{ amountOfService }}</a>
              <ArrowDown2Svg class="ml-1 svg-primary"/>
            </div>
          </template>
          <VisitServicePerToothDropdown :readonly="readonly"
                                        :minAmount="applied"
                                        :service="service"
                                        :teeth="teeth"/>
        </b-dropdown>


<!--        <span v-else>{{ amountOfService }}</span>-->
      </div>
      <div class="col-2 col-td break-all">
        <OneServiceDiscountField :readonly="readonly || applied > 0"
                                 :teeth="teeth"
                                 scope="serviceOfAllTeeth"
                                 predefinedType="absolute"
                                 :service="service"/>
        <span class="mx-2">/</span>
        <OneServiceDiscountField :readonly="readonly || applied > 0"
                                 :teeth="teeth"
                                 scope="serviceOfAllTeeth"
                                 predefinedType="percent"
                                 :service="service"/>
      </div>
      <div class="col-2 col-td break-all">
        {{ formatCurrency(total, true, true) }}
      </div>
      <div class="col-1 col-td text-right d-flex gap-3 justify-content-end">
        <a v-if="!readonly && !applied"
           class="table-btn delete-trash-btn"
           @click="deleteService">
          <TrashSvg class="svg-stroke-red"
                    :class="{ 'svg-size-sm': isMobile }"/>
        </a>
        <a @click="toggleDetails"
           class="pointer details-show-toggler table-btn neutral-btn"
           :class="{ opened: showDetails }">
          <ArrowSvg/>
        </a>
      </div>
      <div class="col-td"
           :class="{ 'col-5': showDetails, 'col-12': !showDetails }">
        <PatientVisitServiceTeethQuantities :service="service"
                                            :view="view"
                                            :readonly="readonly"/>
      </div>
      <div class="col-7 col-td" v-if="showDetails || technicId">
        <div class="d-flex align-items-center gap-5">
          <PatientVisitServiceStaffSelector mode="technic"
                                            :readonly="readonly"
                                            :service="service"/>
          <PatientVisitServiceStaffSelector mode="doctor"
                                            v-if="showDetails"
                                            :readonly="readonly"
                                            :service="service"/>
        </div>
      </div>
    </div>
    <div class="service-collapse d-block"
         ref="collapse"
         :class="{ collapsed: !showDetails }"
         :style="{ height: collapseHeight }">
      <PatientVisitPackagesAndMaterialsNew :readonly="readonly"
                                           :service="service"
      />
    </div>
  </div>
</template>

<script setup>
import TrashSvg from "@/assets/svg-vue/general/delete2.svg"
import ArrowSvg from '@/assets/svg-vue/general/arrow_down.svg'
import {computed, getCurrentInstance, inject, nextTick, onBeforeMount, ref, watch} from "vue"
import {useStore} from "vuex"
import OneServiceCostField from "@/components/parts/patients/visit/teethTab/OneServiceCostField.vue"
import OneServiceDiscountField from "@/components/parts/patients/visit/teethTab/OneServiceDiscountField.vue"
import VisitService from "@/services/visit.service"
import {formatCurrency} from "@/extensions/filters/filters"
import PatientVisitServiceTeethQuantities from "@/components/parts/patients/visit/teethTab/PatientVisitServiceTeethQuantities.vue"
import PatientVisitServiceStaffSelector
  from "@/components/parts/patients/visit/teethTab/PatientVisitServiceStaffSelector.vue"
import PatientVisitPackagesAndMaterialsNew
  from "@/components/parts/patients/visit/teethTab/PatientVisitPackagesAndMaterialsNew.vue"
import {roundToTwoDecimals} from "@/services/number.service"
import {calculateAppliedVsTotalStats} from "@/services/teeth.helper"
import VisitServicePerToothDropdown from "@/components/parts/patients/visit/teethTab/VisitServicePerToothDropdown.vue"
import ArrowDown2Svg from "@/assets/svg-vue/general/arrow_down2.svg.vue"

const props = defineProps({
  service: Object,
  readonly: Boolean,
  view: {
    type: String,
    default: 'visit'
  }
})

const versionCount = inject('versionCount')
const editMode = inject('editMode')

const store = useStore()

const showDetails = ref(false)

const collapsing = ref(false)
const collapse = ref(null)
const collapseHeight = ref('0px')

let i18n = null
let noty = null

const visit = computed(() => store.state.patient.visit)
const teeth = computed(() => store.getters.recordTeethObjects)
const technics = computed(() => props.service.technics || [])
const technicId = computed(() => technics.value.find(d => d.id === props.service.pivot.technic_id))
const serviceIdsWithLabServicesAttached = computed(() => store.getters.serviceIdsWithLabServicesAttached)
const isMobile = computed(() => store.state.dom.isMobile)
const individualTeethServices = computed(() => {
  return (teeth.value || []).map(tooth => (tooth.services || []).filter(obj => obj.id === props.service.id)).flat()
})
const amountOfService = computed(() => teeth.value.reduce((a, b) => a + b.services.filter(s => s.id === props.service.id).length, 0))

const total = computed(() => {
  return roundToTwoDecimals((individualTeethServices.value || []).reduce((acc, serv) => {
    const price = Number((serv.pivot.price_original || 0) - VisitService.calculateDiscountOfService(serv))
    return acc + price
  }, 0))
})

const appliedVsTotalStats = computed(() => {
  if(props.view !== 'planning') return null
  return calculateAppliedVsTotalStats(visit.value, null, props.service.id)
})
const applied = computed(() => appliedVsTotalStats.value?.applied || 0)

onBeforeMount(() => {
  const instance = (getCurrentInstance())
  i18n = instance?.ctx?.$i18n
  noty = instance?.appContext?.app?.noty
})

const deleteService = () => {
  if(serviceIdsWithLabServicesAttached.value.includes(props.service.id)) {
    noty.error(i18n.t('cannot_change_services_that_already_have_laboratory_services_attached'))
    return
  }
  store.dispatch('removeServiceFromToothGroup', {
    serviceId: props.service.id,
    teeth: null,
  })
  versionCount.value++
}

const toggleDetails = () => {
  if(collapsing.value) return
  showDetails.value = !showDetails.value
}

watch(showDetails, async (val) => {
  collapsing.value = true
  setTimeout(() => {
    collapsing.value = false
    if(val) {
      collapseHeight.value = 'auto'
    }
  }, 300)
  await nextTick()
  if (val) {
    collapseHeight.value = collapse.value?.scrollHeight + 'px'
  } else {
    collapseHeight.value = collapse.value?.scrollHeight + 'px'
    requestAnimationFrame(() => {
      collapseHeight.value = '0px'
    })
  }
})
</script>

<style scoped lang="scss">
.details-show-toggler {
  svg {
    transition: all ease-out 0.15s;
  }
  &.opened {
    svg {
      transform: rotate(180deg);
    }
  }
}
.service-collapse {
  transition: height 0.3s ease;
  &.collapsed {
    overflow: hidden;
  }
}
</style>
